import { LDUser } from 'launchdarkly-js-client-sdk';

import { TeacherMe } from '@gmm/sdk/shared';

/**
 * LaunchDarkly SDK v3 single-kind user context. Custom attributes live at the
 * top level so targeting on `org` and `username` still matches.
 */
export type TeacherLDContext = {
  anonymous: false;
  email: string;
  key: string;
  kind: 'user';
  name: string;
  org: string;
  username: string;
};

/**
 * Shared anonymous context used when teacher-context client init never
 * completes. Every user who hits that path shares this key so it counts as
 * one client-side MAU instead of a unique UUID.
 */
export const FALLBACK_ANONYMOUS_LD_CONTEXT = {
  anonymous: true,
  key: 'anonymous',
  kind: 'user',
} as const;

export type FallbackAnonymousLDContext = typeof FALLBACK_ANONYMOUS_LD_CONTEXT;

/**
 * Converts the teacher to the legacy LaunchDarkly user shape used by
 * `Config.identify` and identity state (`custom.org` / `custom.username`).
 *
 * @param teacher Current teacher, or null/undefined before login
 * @returns Legacy LDUser, or undefined when no teacher is available
 * @see https://docs.launchdarkly.com/docs/js-sdk-reference#section-users
 */
export const toLDUser = (teacher?: TeacherMe | null): LDUser | undefined => {
  if (teacher) {
    return {
      anonymous: false,
      custom: {
        org: teacher.organization.name,
        username: teacher.username,
      },
      email: teacher.email,
      key: teacher.id,
      name: `${teacher.firstName} ${teacher.lastName}`,
    };
  }

  // undefined so the client SDK is not initialized until a teacher exists
  return undefined;
};

/**
 * Converts the teacher to an SDK v3 context for `LDProvider`. Attributes that
 * were nested under `custom` on {@link toLDUser} are top-level here.
 *
 * @param teacher Current teacher, or null/undefined before login
 * @returns v3 user context, or undefined when no teacher is available
 */
export const toLDContext = (
  teacher?: TeacherMe | null,
): TeacherLDContext | undefined => {
  if (!teacher) {
    return undefined;
  }

  return {
    anonymous: false,
    email: teacher.email,
    key: teacher.id,
    kind: 'user',
    name: `${teacher.firstName} ${teacher.lastName}`,
    org: teacher.organization.name,
    username: teacher.username,
  };
};

// This is a constant for multivariate flags when it should be off
const NONE = 'none';

export const isFlagOff = (value?: any): boolean => {
  switch (typeof value) {
    case 'string':
      return value.toLowerCase() === NONE;
    case 'boolean':
      return false;
    default:
      return !value;
  }
};

const regexCoerce = /(^|[^\d])(\d{1,16})/;

export const coerce = (version: string | null): number => {
  const versionMatch = version && version.match(regexCoerce);

  return versionMatch === null ? 0 : Number(versionMatch[2]);
};
